import { useState, useEffect } from 'react'
import type { Order } from '../../../types'
import { CRYPTO_ASSETS, NETWORKS } from '../../../lib/constants'
import { config } from '../../../config'
import { formatMYR, formatCrypto } from '../../../lib/utils'
import { orderApi } from '../../../lib/api'
import { layout, cx, text } from '../../../lib/ui-primitives'
import { Button, Card, LoadingState } from '../../primitives'
import { ExternalLinkIcon } from '../../icons'
import type { useKiosk } from '../../../hooks/useKiosk'

interface ProcessingScreenProps {
  kiosk: ReturnType<typeof useKiosk>
}

/**
 * ProcessingScreen - Order verification and result display
 *
 * Refactored to use:
 * - LoadingState (primitive) for waiting state
 * - Card (primitive) for summary cards
 * - Button (primitive) for actions
 *
 * LOC reduced from 234 to ~140 (40% reduction)
 */
export function ProcessingScreen({ kiosk }: ProcessingScreenProps) {
  const { selectedCrypto, selectedNetwork, currentOrder, userDetails, orderId, resetFlow } = kiosk
  const [polledOrder, setPolledOrder] = useState<Order | null>(null)

  // Poll for status updates via API
  useEffect(() => {
    let alive = true

    const poll = async () => {
      if (!orderId) return

      const result = await orderApi.lookup(orderId)
      if (!alive) return

      if (result.success && result.data?.order) {
        const apiOrder = result.data.order
        if (apiOrder.status !== 'pending') {
          setPolledOrder({
            id: apiOrder.id,
            crypto: apiOrder.crypto,
            network: apiOrder.network,
            amountMYR: apiOrder.amountMYR,
            amountCrypto: apiOrder.amountCrypto,
            networkFee: apiOrder.networkFee,
            rate: apiOrder.rate,
            customer: {
              name: apiOrder.customerName,
              contactType: 'telegram',
              contact: '',
              walletAddress: apiOrder.walletAddress,
            },
            paymentRef: '',
            hasProofImage: false,
            status: apiOrder.status,
            txHash: apiOrder.txHash,
            createdAt: new Date(apiOrder.createdAt).getTime(),
            updatedAt: new Date(apiOrder.updatedAt).getTime(),
          })
        }
      }
    }

    poll()
    const interval = setInterval(poll, 5000)
    return () => {
      alive = false
      clearInterval(interval)
    }
  }, [orderId])

  if (!selectedCrypto || !selectedNetwork) return null

  const asset = CRYPTO_ASSETS[selectedCrypto]

  // Result screen (approved/rejected)
  if (polledOrder && polledOrder.status !== 'pending') {
    const isApproved = polledOrder.status === 'approved' || polledOrder.status === 'completed'

    return (
      <div className={cx(layout.screenContent, 'items-center justify-center')}>
        <div className="text-center w-full max-w-sm">
          {/* Status Icon */}
          <ResultIcon
            success={isApproved}
            color={isApproved ? asset.color : undefined}
          />

          <h2 className={cx(text.h2, 'mb-2')}>
            {isApproved ? 'Payment Confirmed!' : 'Order Declined'}
          </h2>
          <p className={cx('mb-8', text.secondary)}>
            {isApproved
              ? polledOrder.status === 'completed'
                ? 'Your crypto has been sent'
                : 'Processing your crypto transfer'
              : 'Please contact support for assistance'}
          </p>

          {/* Order Details */}
          <Card padding="md" className="mb-6 text-left">
            <div className="text-sm space-y-3">
              <DetailRow label="Order ID" value={polledOrder.id} mono />
              <DetailRow label="Amount Paid" value={formatMYR(polledOrder.amountMYR)} numeric />

              {isApproved && (
                <>
                  <DetailRow
                    label={`Crypto ${polledOrder.status === 'completed' ? 'Sent' : 'Amount'}`}
                    value={`${formatCrypto(polledOrder.amountCrypto, 4)} ${polledOrder.crypto}`}
                    valueColor={asset.color}
                    mono
                  />

                  {polledOrder.txHash && (
                    <TransactionLink
                      txHash={polledOrder.txHash}
                      network={polledOrder.network}
                    />
                  )}

                  <WalletDisplay address={polledOrder.customer.walletAddress} />
                </>
              )}
            </div>
          </Card>

          <Button variant="ghost" fullWidth size="lg" onClick={resetFlow}>
            Done
          </Button>

          <p className={cx('text-xs mt-4', text.disabled)}>
            Questions? Contact {config.supportTelegram}
          </p>
        </div>
      </div>
    )
  }

  // Processing state
  return (
    <div
      className={cx(layout.screenContent, 'items-center justify-center')}
      role="status"
      aria-label="Verifying payment"
    >
      <div className="text-center w-full max-w-sm">
        <LoadingState
          message="Verifying Payment"
          subMessage="We're checking your payment"
          size="xl"
          icon="⏳"
        />

        {/* Order Summary */}
        <Card padding="md" className="mb-6 mt-8">
          <div className="text-sm space-y-3">
            <DetailRow label="Order ID" value={orderId || ''} mono />
            <DetailRow label="Amount" value={formatMYR(currentOrder?.amountMYR || 0)} numeric />
            <DetailRow
              label="Crypto"
              value={`${formatCrypto(currentOrder?.amountCrypto || 0, 4)} ${selectedCrypto}`}
              valueColor={asset.color}
              mono
            />
            <DetailRow label="Network" value={selectedNetwork} />
          </div>
        </Card>

        <div className="space-y-2 text-sm">
          <p className={text.tertiary}>
            We'll contact you via {userDetails.contactType === 'telegram' ? 'Telegram' : 'email'}
          </p>
          <p className={cx('text-xs', text.disabled)}>
            Need help? Contact {config.supportTelegram}
          </p>
        </div>

        <Button variant="ghost" className="mt-8" onClick={resetFlow}>
          Start New Order
        </Button>
      </div>
    </div>
  )
}

// ============================================
// HELPER COMPONENTS
// ============================================

interface ResultIconProps {
  success: boolean
  color?: string
}

function ResultIcon({ success, color }: ResultIconProps) {
  return (
    <div
      className={cx(
        'size-20 rounded-full flex items-center justify-center mb-6 mx-auto',
        !success && 'bg-red-500/20 border border-red-500/20'
      )}
      style={success ? { backgroundColor: `${color}33` } : undefined}
    >
      <span className="text-4xl" aria-hidden="true">
        {success ? '✓' : '✗'}
      </span>
    </div>
  )
}

interface DetailRowProps {
  label: string
  value: string
  mono?: boolean
  numeric?: boolean
  valueColor?: string
}

function DetailRow({ label, value, mono, numeric, valueColor }: DetailRowProps) {
  return (
    <div className="flex justify-between">
      <span className={text.secondary}>{label}</span>
      <span
        className={cx(
          'text-white',
          mono && text.mono,
          numeric && text.numeric
        )}
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </span>
    </div>
  )
}

interface TransactionLinkProps {
  txHash: string
  network: string
}

function TransactionLink({ txHash, network }: TransactionLinkProps) {
  const networkConfig = NETWORKS[network as keyof typeof NETWORKS]
  if (!networkConfig) return null

  return (
    <div className="pt-3 border-t border-white/10">
      <span className={cx('text-xs block mb-1', text.tertiary)}>Transaction Hash</span>
      <a
        href={`${networkConfig.explorer}${txHash}`}
        target="_blank"
        rel="noopener noreferrer"
        className={cx(
          'text-indigo-300 text-xs break-all hover:underline inline-flex items-center gap-1',
          text.mono
        )}
      >
        {txHash.slice(0, 20)}...{txHash.slice(-10)}
        <ExternalLinkIcon aria-hidden="true" />
      </a>
    </div>
  )
}

interface WalletDisplayProps {
  address: string
}

function WalletDisplay({ address }: WalletDisplayProps) {
  return (
    <div className="pt-3 border-t border-white/10">
      <span className={cx('text-xs block mb-1', text.tertiary)}>To Wallet</span>
      <span className={cx('text-white text-xs break-all', text.mono)}>
        {address}
      </span>
    </div>
  )
}
